'use client';

import { useState, useEffect } from 'react';
import { TextField, InputAdornment, Box } from '@mui/material';
import { SearchIcon2, LocationAutocomplete } from '@/components';
import { ButtonDefault } from '@/components/shared/UI/ButtonDefault/ButtonDefault';
import { SearchContainer } from './styles';
import { useTranslations } from 'next-intl';
import { COLORS } from '@/constants';
import { ReferenceLocation } from '@/types';
import { buildLocationFromUrlParams } from '../../utils';
import { useBrokerFilters } from '../../hooks/useBrokerFilters';
import { buildLocationQuery } from './utils';

export const BrokerSearch = () => {
  const _t = useTranslations();
  const { filters, setFilters } = useBrokerFilters();

  const [locationList, setLocationList] = useState<ReferenceLocation[]>(() =>
    buildLocationFromUrlParams(filters),
  );
  const location = locationList[0] ?? null;
  const [name, setName] = useState(filters.name);

  useEffect(() => {
    setName(filters.name);
  }, [filters.name]);

  useEffect(() => {
    setLocationList(buildLocationFromUrlParams(filters));
  }, [
    filters.locType,
    filters.locCode,
    filters.locName,
    filters.locPostcode,
    filters.town,
    filters.zip,
  ]);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const search = (selected: ReferenceLocation | null) => {
    void setFilters({
      name: name.trim() || null,
      ...buildLocationQuery(selected),
    });
  };

  const onSearch = () => search(location);

  const onLocationChange = (next: ReferenceLocation[]) => {
    const single = next.length > 1 ? next.slice(-1) : next;
    setLocationList(single);
    search(single[0] ?? null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <SearchContainer className="safari-no-autofill-ui">
      <LocationAutocomplete
        value={locationList}
        onChange={onLocationChange}
        placeholder={_t('brokerListPage.locationPlaceholder')}
        inputSx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            height: '48px',
            '& fieldset': { borderColor: COLORS['neutral/300'] },
            '&:hover fieldset': { borderColor: COLORS['neutral/300'] },
            '&.Mui-focused fieldset': { borderColor: COLORS['neutral/300'] },
          },
        }}
      />
      <TextField
        placeholder={_t('brokerListPage.namePlaceholder')}
        fullWidth
        size="small"
        value={name}
        onChange={onNameChange}
        onKeyDown={(e) => onKeyDown(e)}
        slotProps={{
          htmlInput: {
            autoComplete: 'off',
            name: 'broker-search-name',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SearchIcon2 width="20px" height="20px" />
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            height: '48px',
            '& fieldset': {
              borderColor: COLORS['neutral/300'],
            },
            '& fieldset:hover': {
              borderColor: COLORS['neutral/300'],
            },
            '&.Mui-focused fieldset': {
              borderColor: COLORS['neutral/300'],
            },
          },
        }}
      />
      <ButtonDefault
        variant="contained"
        onClick={onSearch}
        theme="accent"
        sx={{
          height: '48px',
          minWidth: '140px',
          backgroundColor: `${COLORS['neutral/200']} !important`,
          color: COLORS['neutral/700'],
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }}
      >
        {_t('brokerListPage.findBrokerButton')}
      </ButtonDefault>
    </SearchContainer>
  );
};
